import {
  Stethoscope,
  FileText,
  FileBadge,
  MessageSquareText,
  Search,
  ClipboardCheck,
  ShieldCheck,
  Eye,
  Scale,
  Clock,
  CalendarClock,
  Video,
  ClipboardEdit,
  HeartPulse,
  Layers,
} from "lucide-react";

export const services = {
  "entrevista-medica-qualificada": {
    slug: "entrevista-medica-qualificada",
    icon: Stethoscope,
    variant: "deepBlue",
    name: "Entrevista Médica Qualificada",
    seoTitle: "Entrevista Médica Qualificada para Operadoras | Previsa Saúde",
    seoDesc:
      "Entrevista médica qualificada conduzida por enfermeiros avaliadores: análise da Declaração de Saúde, apuração clínica, CID e parecer técnico para operadoras de planos de saúde.",
    h1: "Entrevista Médica Qualificada",
    intro:
      "A entrevista médica qualificada é conduzida por enfermeiros avaliadores no momento da adesão ao plano de saúde. Ela combina escuta ativa, análise da Declaração de Saúde e apuração clínica para qualificar o risco assistencial desde a contratação.",
    intro2:
      "Cada atendimento gera um parecer técnico com recomendação e CID apurado, dando à operadora uma base objetiva e rastreável para suas decisões.",
    steps: [
      { icon: CalendarClock, title: "Agendamento ou fila", desc: "O beneficiário é atendido por agendamento ou fila em tempo real, conforme o fluxo da operadora.", variant: "deepBlue" },
      { icon: Video, title: "Videoatendimento gravado", desc: "A entrevista é realizada por vídeo, com gravação, histórico e trilha de evidência.", variant: "vividBlue" },
      { icon: Search, title: "Apuração clínica", desc: "O enfermeiro avaliador investiga condições, tratamentos e morbidades com base na Declaração de Saúde.", variant: "aqua" },
      { icon: ClipboardCheck, title: "Parecer técnico", desc: "A operadora recebe um relatório objetivo com recomendação e CID apurado.", variant: "deepBlue" },
    ],
    benefits: [
      { icon: Scale, title: "Menos subjetividade", desc: "Critérios padronizados reduzem ruído entre áreas e tornam as decisões mais consistentes.", variant: "deepBlue" },
      { icon: Eye, title: "Rastreabilidade completa", desc: "Gravação, histórico e documentação de cada atendimento.", variant: "vividBlue" },
      { icon: ShieldCheck, title: "Decisões mais defensáveis", desc: "Base documental que mitiga conflitos e judicialização.", variant: "aqua" },
      { icon: Clock, title: "Agilidade operacional", desc: "Tempo médio de 15 minutos por entrevista e capacidade de 6.000 atendimentos por mês.", variant: "vividBlue" },
    ],
    related: ["analise-declaracao-saude", "gestao-cpt"],
  },
  "analise-declaracao-saude": {
    slug: "analise-declaracao-saude",
    icon: FileText,
    variant: "vividBlue",
    name: "Análise da Declaração de Saúde",
    seoTitle: "Análise da Declaração de Saúde (DS) | Previsa Saúde",
    seoDesc:
      "Apoio ao preenchimento e análise técnica da Declaração de Saúde (DS) por enfermeiros: identificação de doenças e lesões preexistentes, consistência das informações e base documental para operadoras.",
    h1: "Análise da Declaração de Saúde",
    intro:
      "A Declaração de Saúde (DS) é a base da relação entre beneficiário e operadora. A Previsa Saúde apoia o correto preenchimento e realiza a leitura técnica das informações declaradas, com precisão e responsabilidade.",
    intro2:
      "O trabalho é conduzido por enfermeiros com domínio de anatomia, fisiologia e CID-10, garantindo consistência entre o que foi declarado e o que foi apurado na entrevista.",
    steps: [
      { icon: ClipboardEdit, title: "Apoio ao preenchimento", desc: "Orientação ao proponente para o preenchimento correto e completo da DS.", variant: "deepBlue" },
      { icon: Search, title: "Leitura técnica", desc: "Análise criteriosa de históricos, procedimentos, tratamentos e condições de saúde.", variant: "vividBlue" },
      { icon: HeartPulse, title: "Identificação de DLP", desc: "Apuração de doenças e lesões preexistentes com correlação clínica e CID-10.", variant: "aqua" },
      { icon: ClipboardCheck, title: "Registro consistente", desc: "Informações registradas com qualidade, consistência e confidencialidade.", variant: "deepBlue" },
    ],
    benefits: [
      { icon: ShieldCheck, title: "Menos conflito e judicialização", desc: "Base documental sólida para decisões mais defensáveis.", variant: "deepBlue" },
      { icon: Scale, title: "Consistência técnica", desc: "Critérios padronizados aplicados a todas as análises.", variant: "vividBlue" },
      { icon: Layers, title: "Visão completa do risco", desc: "A DS analisada alimenta a inteligência de carteira da operadora.", variant: "aqua" },
    ],
    related: ["entrevista-medica-qualificada", "gestao-cpt"],
  },
  "gestao-cpt": {
    slug: "gestao-cpt",
    icon: FileBadge,
    variant: "aqua",
    name: "Gestão de CPT",
    seoTitle: "Gestão de CPT — Cobertura Parcial Temporária | Previsa Saúde",
    seoDesc:
      "Apoio técnico para identificação e gestão de CPT (cobertura parcial temporária): critérios padronizados, parecer técnico e rastreabilidade para operadoras de planos de saúde.",
    h1: "Gestão de CPT — Cobertura Parcial Temporária",
    intro:
      "A cobertura parcial temporária (CPT) exige critério técnico e documentação consistente. A Previsa Saúde apoia a operadora na identificação de situações de CPT quando aplicável, com base na entrevista qualificada e na análise da Declaração de Saúde.",
    intro2:
      "Cada recomendação é fundamentada em apuração clínica e registrada com trilha de evidência, dando segurança técnica e jurídica à decisão.",
    steps: [
      { icon: MessageSquareText, title: "Entrevista qualificada", desc: "A apuração clínica identifica condições e morbidades relevantes para a análise de CPT.", variant: "deepBlue" },
      { icon: Search, title: "Correlação técnica", desc: "As informações são correlacionadas com a Declaração de Saúde e o CID-10.", variant: "vividBlue" },
      { icon: ClipboardCheck, title: "Recomendação fundamentada", desc: "Parecer técnico objetivo indicando as situações em que a CPT é aplicável.", variant: "aqua" },
    ],
    benefits: [
      { icon: Scale, title: "Critérios padronizados", desc: "Aplicação consistente e auditável dos critérios de CPT.", variant: "deepBlue" },
      { icon: ShieldCheck, title: "Segurança jurídica", desc: "Documentação e evidência para decisões mais defensáveis.", variant: "vividBlue" },
      { icon: Eye, title: "Rastreabilidade", desc: "Histórico completo de cada análise, do atendimento ao parecer.", variant: "aqua" },
    ],
    related: ["entrevista-medica-qualificada", "analise-declaracao-saude"],
  },
};

export const serviceList = Object.values(services);
